/**
 * Tour scripts per page.
 *
 * Each export is an array of driver.js step configs. Steps with no
 * `element` selector render as centered text overlays.
 *
 * Selectors point at `data-testid` attributes already in the codebase to
 * keep the tour resilient to design changes.
 */

export const dashboardTour = [
    {
        popover: {
            title: "Bienvenido a LicitaIA",
            description:
                "Te enseñamos en 30 segundos cómo funciona el centro de mando. Puedes pulsar Atrás o cerrar en cualquier momento.",
        },
    },
    {
        element: "[data-testid='dashboard-kpis']",
        popover: {
            title: "KPIs en tiempo real",
            description:
                "Aquí ves cuántas licitaciones tienes en curso, presentadas, ganadas y el importe acumulado.",
        },
    },
    {
        element: "[data-testid='nav-plcsp']",
        popover: {
            title: "Buscar licitaciones",
            description:
                "Desde aquí accedes a las licitaciones públicas (PLCSP y autonómicas) en tiempo real.",
        },
    },
    {
        element: "[data-testid='nav-crm']",
        popover: {
            title: "CRM de licitaciones",
            description:
                "Todas tus licitaciones presentadas, ordenadas por estado. Arrastra cards entre columnas para registrar resultados.",
        },
    },
    {
        element: "[data-testid='nav-memoria-tecnica']",
        popover: {
            title: "Memoria técnica con IA",
            description:
                "Sube los pliegos y la IA redacta la memoria respetando todas las restricciones del pliego.",
        },
    },
];

export const plcspTour = [
    {
        popover: {
            title: "Buscador de licitaciones",
            description:
                "Tienes acceso al PLCSP del Estado y a todas las plataformas autonómicas integradas en una sola búsqueda.",
        },
    },
    {
        element: "[data-testid='plcsp-search-btn']",
        popover: {
            title: "Búsqueda por palabras",
            description:
                "Escribe palabras clave del contrato que buscas (ej: 'limpieza hospital', 'mantenimiento jardines').",
        },
    },
    {
        element: "[data-testid='plcsp-filters']",
        popover: {
            title: "Filtros avanzados",
            description:
                "Filtra por comunidad autónoma, tipo de contrato (obras / suministros / servicios), importe y fecha.",
        },
    },
    {
        element: "[data-testid='plcsp-ai-toggle']",
        popover: {
            title: "Búsqueda con IA",
            description:
                "Describe en lenguaje natural lo que buscas y la IA encuentra las licitaciones más relevantes para tu empresa.",
        },
    },
];

export const licitacionesTour = [
    {
        popover: {
            title: "Mis licitaciones",
            description:
                "Aquí ves todas las licitaciones que has importado o creado manualmente.",
        },
    },
    {
        element: "[data-testid='new-tender-btn']",
        popover: {
            title: "Nueva licitación",
            description:
                "Crea una licitación manualmente si no estaba en el buscador (por ejemplo, una contratación menor).",
        },
    },
    {
        element: "[data-testid='tenders-filter-status']",
        popover: {
            title: "Filtros rápidos",
            description:
                "Filtra por estado para ver solo las que están en borrador, en preparación o presentadas.",
        },
    },
];

export const tenderDetailTour = [
    {
        popover: {
            title: "Ficha de licitación",
            description:
                "Aquí tienes todo: análisis IA, escandallo de costes, anexos, memoria técnica y chat con la IA del pliego.",
        },
    },
    {
        element: "[data-testid='presentar-licitacion-btn']",
        popover: {
            title: "Presentar al CRM",
            description:
                "Cuando hayas presentado la licitación, pulsa este botón. Pasará automáticamente al CRM en 'En evaluación'.",
        },
    },
    {
        element: "[data-testid='prepare-bundle-btn']",
        popover: {
            title: "Paquete IA",
            description:
                "Genera todos los anexos obligatorios del pliego con un solo clic.",
        },
    },
    {
        element: "[data-testid='tender-tabs']",
        popover: {
            title: "Pestañas",
            description:
                "Análisis, Escandallo, Checklist, Memoria técnica y Chat IA del pliego. Cada pestaña una herramienta.",
        },
    },
];

export const crmTour = [
    {
        popover: {
            title: "CRM de licitaciones",
            description:
                "Todas tus licitaciones presentadas se organizan por estado en este Kanban. Arrastra para cambiar resultados.",
        },
    },
    {
        element: "[data-testid='crm-kpis']",
        popover: {
            title: "KPIs del pipeline",
            description:
                "Total presentadas, win rate, importe ganado y baja media. Se recalculan con los filtros activos.",
        },
    },
    {
        element: "[data-testid='crm-toolbar']",
        popover: {
            title: "Filtros avanzados",
            description:
                "Filtra por comunidad, órgano, período, importe, baja % y estado. Búsqueda libre y exporta a CSV.",
        },
    },
    {
        element: "[data-testid='crm-kanban']",
        popover: {
            title: "Kanban con drag-and-drop",
            description:
                "Arrastra una card a 'Adjudicadas' o 'Perdidas' para registrar el resultado. La IA te pedirá los datos.",
        },
    },
    {
        element: "[data-testid='crm-view-list']",
        popover: {
            title: "Vista de tabla",
            description:
                "Cambia a tabla para comparar todas las licitaciones en una sola pantalla con todos los datos visibles.",
        },
    },
];

export const memoriaStandaloneTour = [
    {
        popover: {
            title: "Memoria técnica con IA",
            description:
                "Sube los pliegos de cualquier licitación y la IA redacta la memoria técnica respetando todas las restricciones.",
        },
    },
    {
        element: "[data-testid='ms-new-btn']",
        popover: {
            title: "Nueva memoria",
            description:
                "Pulsa aquí, sube los PDFs del pliego y dale un nombre. La IA extraerá las restricciones (tipografía, páginas, secciones) y redactará la memoria.",
        },
    },
];

export const cumplimentarAnexosTour = [
    {
        popover: {
            title: "Cumplimentar anexos en blanco",
            description:
                "Sube un anexo en blanco que te pida la administración y la IA lo rellena con tus datos de empresa.",
        },
    },
    {
        element: "[data-testid='anexos-uploader']",
        popover: {
            title: "Sube el anexo",
            description:
                "Arrastra el PDF/DOCX en blanco. La IA detectará los campos y los completará con tu perfil empresa.",
        },
    },
];

export const legalTour = [
    {
        popover: {
            title: "Auxiliar legal",
            description:
                "Extrae plazos preclusivos del pliego (recursos, alegaciones, subsanaciones) y te avisa con tiempo.",
        },
    },
    {
        element: "[data-testid='legal-extract-btn']",
        popover: {
            title: "Extraer plazos",
            description:
                "La IA lee los pliegos y extrae automáticamente todos los plazos legales con su fecha exacta.",
        },
    },
];

export const analiticasTour = [
    {
        popover: {
            title: "Analytics",
            description:
                "Métricas avanzadas: win rate por comunidad/órgano, evolución temporal y comparativa con tu sector.",
        },
    },
];

export const perfilEmpresaTour = [
    {
        popover: {
            title: "Perfil de empresa",
            description:
                "Datos fiscales, solvencia económica y técnica, certificados. Todo lo que la IA necesita para autocompletar anexos.",
        },
    },
];
